import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ReadableAccordion';
import { ArrowRight, CheckCircle, ClipboardCheck, Mail, MapPin, Phone, ShieldCheck, Star, Wrench, Zap } from 'lucide-react';
import InlineBookingForm from './InlineBookingForm';
import { HeroProofList } from './HeroProofList';
import { ProofMetric } from './ProofMetric';
import { TechnicianNetworkCta } from './TechnicianNetworkCta';
import { brandSeoPages } from '../data/seoBrandPages';
import { companyInfo, contactInfo, googleReviewsData } from '../data/mockData';
import { trackEvent } from '../utils/analytics';
import { setPageSeo } from '../utils/seo';
import { scrollToBookingForm } from '../utils/bookingFormHighlight';

const kebab = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const buildBrandSchema = (page) => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://repair.3dprintergear.com.au/' },
        { '@type': 'ListItem', position: 2, name: page.h1, item: page.canonical }
      ]
    },
    {
      '@type': 'WebPage',
      '@id': `${page.canonical}#webpage`,
      url: page.canonical,
      name: page.title,
      description: page.description,
      isPartOf: { '@id': 'https://repair.3dprintergear.com.au/#website' },
      about: { '@id': 'https://repair.3dprintergear.com.au/#business' },
      primaryImageOfPage: { '@type': 'ImageObject', url: page.image },
      inLanguage: 'en-AU'
    },
    {
      '@type': 'Service',
      '@id': `${page.canonical}#service`,
      name: page.h1,
      serviceType: `${page.brandName} 3D Printer Repair`,
      provider: { '@id': 'https://repair.3dprintergear.com.au/#business' },
      brand: { '@type': 'Brand', name: page.brandName },
      areaServed: { '@type': 'Country', name: 'Australia' },
      availableChannel: {
        '@type': 'ServiceChannel',
        servicePhone: { '@type': 'ContactPoint', telephone: '+611300334327', contactType: 'Repairs and service bookings' },
        serviceUrl: page.canonical
      },
      description: page.heroSubheading,
      offers: { '@type': 'Offer', availability: 'https://schema.org/InStock', priceCurrency: 'AUD', description: 'Free quote before repair work begins.' }
    },
    {
      '@type': 'FAQPage',
      '@id': `${page.canonical}#faq`,
      mainEntity: page.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: { '@type': 'Answer', text: faq.answer }
      }))
    }
  ]
});

export default function SeoBrandPage({ brandKey = 'bambuLab' }) {
  const page = brandSeoPages[brandKey];
  const bookingFormRef = useRef(null);
  const [formInView, setFormInView] = useState(true);
  const [bookingPrefill, setBookingPrefill] = useState({ printerBrand: page.prefill.printerBrand, autoApply: true, expand: false });
  const schema = useMemo(() => buildBrandSchema(page), [buildBrandSchema, page]);

  useEffect(() => {
    setPageSeo({ title: page.title, description: page.description, canonical: page.canonical, image: page.image });
  }, [page, setPageSeo]);

  useEffect(() => {
    const formNode = bookingFormRef.current;
    if (!formNode) return undefined;
    const observer = new IntersectionObserver(([entry]) => setFormInView(entry.isIntersecting), { threshold: 0.2 });
    observer.observe(formNode);
    return () => observer.disconnect();
  }, [bookingFormRef]);

  const scrollToBooking = (location, label = 'Start repair request', prefill = {}) => {
    trackEvent('cta_click', { cta_location: location, cta_label: label });
    setBookingPrefill({ ...page.prefill, ...prefill, sequence: Date.now() });
    scrollToBookingForm(bookingFormRef);
  };

  const handlePhoneClick = (phoneLocation) => trackEvent('phone_click', { phone_location: phoneLocation });

  return (
    <div className="min-h-screen bg-white text-gray-900 pb-20 md:pb-0" data-testid={`seo-brand-page-${page.slug}`}>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200" data-testid="brand-page-header">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-testid="brand-header-home-link">
              <img src={companyInfo.logo} alt={companyInfo.name} className="h-10 sm:h-12 w-auto" data-testid="brand-header-logo-image" />
              <div className="hidden sm:block">
                <p className="text-sm font-extrabold tracking-tight" data-testid="brand-header-company-name">{companyInfo.name}</p>
                <p className="text-xs text-gray-500" data-testid="brand-header-company-division">Service &amp; Repair Division</p>
              </div>
            </Link>
            <div className="flex items-center gap-3 sm:gap-5">
              <a href="tel:1300334327" onClick={() => handlePhoneClick(`${page.slug}_header`)} className="hidden md:flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-black transition-colors" data-testid="brand-header-phone-link">
                <Phone className="h-4 w-4" />
                <span data-testid="brand-header-phone-text">{contactInfo.phone} <span className="text-gray-400">·</span> Option 2</span>
              </a>
              <button onClick={() => scrollToBooking(`${page.slug}_header`, 'Book Service')} className="bg-gray-900 hover:bg-black text-white text-sm font-bold px-3 sm:px-5 py-2.5 rounded-sm border-b-4 border-orange-500 hover:border-orange-400 transition-colors whitespace-nowrap" data-testid="brand-header-book-button">
                <span className="sm:hidden">Book</span><span className="hidden sm:inline">Book Service</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main>
        <section className="border-b border-gray-200 bg-white" data-testid="brand-hero-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8 lg:py-14">
            <div className="grid gap-8 lg:grid-cols-12 lg:gap-12 items-start">
              <div className="lg:col-span-5">
                <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-gray-500 mb-3" data-testid="brand-hero-eyebrow">{page.eyebrow}</p>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02] mb-4" data-testid="brand-hero-heading">{page.h1}</h1>
                <p className="text-base text-gray-600 leading-relaxed max-w-xl mb-5" data-testid="brand-hero-subheading">{page.heroSubheading}</p>
                <HeroProofList items={page.proof} testId="brand-hero-proof-list" />
                <div className="border border-gray-200 overflow-hidden rounded-sm" data-testid="brand-hero-image-wrap">
                  <img src={page.image} alt="Bambu Lab X1 Carbon repair technician" className="w-full aspect-[16/9] object-cover object-center" data-testid="brand-hero-image" />
                </div>
              </div>
              <div className="lg:col-span-7 lg:sticky lg:top-24" data-testid="brand-booking-column">
                <InlineBookingForm key={bookingPrefill?.sequence || `${brandKey}-booking-form`} ref={bookingFormRef} prefill={bookingPrefill} initialPrinterBrand={page.prefill.printerBrand} pageSource={page.h1} />
              </div>
            </div>
          </div>
        </section>

        <section className="bg-gray-900 text-white" data-testid="brand-proof-strip">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 py-4">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-gray-700 border border-gray-700">
              {[
                ['Bambu', 'Specialist support'],
                ['X1/P1/A1', 'Model coverage'],
                ['AMS', 'Feed issue triage'],
                ['Insured', 'PI + public liability'],
                ['700+', 'Google reviews']
              ].map(([number, label]) => <ProofMetric key={label} number={number} label={label} testIdPrefix="brand-proof-metric" />)}
            </div>
          </div>
        </section>

        <section className="py-14 bg-white" data-testid="brand-models-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-4">
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3" data-testid="brand-models-heading">Bambu Lab models we support</h2>
                <p className="text-base text-gray-600 leading-relaxed mb-5" data-testid="brand-models-summary">Your repair options depend on the printer series, AMS setup and symptoms. Include your exact Bambu Lab model when requesting a quote.</p>
                <button onClick={() => scrollToBooking(`${page.slug}_models`, 'Start Bambu repair request')} className="bg-gray-900 hover:bg-black text-white text-sm font-bold px-5 py-2.5 rounded-sm border-b-4 border-orange-500 hover:border-orange-400 transition-colors" data-testid="brand-models-cta-button">Start Bambu repair request</button>
              </div>
              <div className="lg:col-span-8 grid sm:grid-cols-2 gap-3">
                {page.models.map((item) => (
                  <article key={item.model} className="border border-gray-200 bg-gray-50 p-5 rounded-sm" data-testid={`brand-model-card-${kebab(item.model)}`}>
                    <p className="text-lg font-extrabold mb-2" data-testid={`brand-model-title-${kebab(item.model)}`}>{item.model}</p>
                    <p className="text-sm text-gray-600 leading-relaxed" data-testid={`brand-model-copy-${kebab(item.model)}`}>{item.focus}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 bg-gray-50 border-y border-gray-200" data-testid="brand-issues-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-4">
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3" data-testid="brand-issues-heading">Common Bambu Lab repair issues</h2>
                <p className="text-base text-gray-600 leading-relaxed" data-testid="brand-issues-summary">If the same fault keeps returning after basic troubleshooting, it is usually time for structured diagnosis and quote-before-work repair support.</p>
              </div>
              <div className="lg:col-span-8 grid sm:grid-cols-2 gap-3">
                {page.issues.map((issue) => (
                  <button key={issue.title} onClick={() => scrollToBooking(`${page.slug}_issue`, issue.title, { issueDescription: `I need help with a Bambu Lab issue: ${issue.title}.` })} className="text-left border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-900 p-5 rounded-sm transition-colors" data-testid={`brand-issue-button-${kebab(issue.title)}`}>
                    <Zap className="h-5 w-5 text-orange-500 mb-3" />
                    <span className="block text-sm font-extrabold text-gray-900 mb-2" data-testid={`brand-issue-title-${kebab(issue.title)}`}>{issue.title}</span>
                    <span className="block text-sm text-gray-600 leading-relaxed" data-testid={`brand-issue-copy-${kebab(issue.title)}`}>{issue.description}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 bg-white" data-testid="brand-workflow-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-4">
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3" data-testid="brand-workflow-heading">Bambu repair workflow</h2>
                <p className="text-base text-gray-600 leading-relaxed" data-testid="brand-workflow-summary">The goal is to avoid guesswork: diagnose the fault, quote clearly, repair the right component and verify the printer where practical.</p>
              </div>
              <div className="lg:col-span-8 grid md:grid-cols-4 gap-px bg-gray-200 border border-gray-200" data-testid="brand-workflow-grid">
                {page.workflow.map(([step, copy], index) => (
                  <article key={step} className="bg-white p-5" data-testid={`brand-workflow-step-${kebab(step)}`}>
                    <p className="text-3xl font-extrabold mb-4" data-testid={`brand-workflow-step-number-${index + 1}`}>{index + 1}</p>
                    <h3 className="text-base font-extrabold mb-2" data-testid={`brand-workflow-step-title-${kebab(step)}`}>{step}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed" data-testid={`brand-workflow-step-copy-${kebab(step)}`}>{copy}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 bg-gray-50 border-y border-gray-200" data-testid="brand-trust-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="grid lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-5">
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3" data-testid="brand-trust-heading">Trusted Bambu Lab support for serious users</h2>
                <p className="text-base text-gray-600 leading-relaxed mb-5" data-testid="brand-trust-summary">Bambu Lab printers are fast, enclosed and highly integrated. When they fail, professional diagnosis helps protect the printer, the workflow and the parts budget.</p>
                <div className="border border-gray-200 rounded-sm overflow-hidden bg-white" data-testid="brand-trust-image-wrap">
                  <img src={page.image} alt="Bambu Lab X1 Carbon repair support" className="w-full aspect-[4/3] object-cover object-center" data-testid="brand-trust-image" />
                </div>
              </div>
              <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
                {[
                  [ShieldCheck, 'Fully insured', 'Professional indemnity and public liability cover.'],
                  [Wrench, 'Repair-centre backed', 'Specialist diagnostics and repair workflow.'],
                  [ClipboardCheck, 'Quote-before-work', 'No repair begins until the quote is clear.'],
                  [CheckCircle, 'Schools and businesses', 'Support for serious users, printer fleets and repeat maintenance.']
                ].map(([Icon, title, copy]) => (
                  <article key={title} className="border border-gray-200 bg-white p-5 rounded-sm" data-testid={`brand-trust-card-${kebab(title)}`}>
                    <Icon className="h-6 w-6 text-gray-900 mb-3" />
                    <h3 className="text-base font-extrabold mb-1" data-testid={`brand-trust-card-title-${kebab(title)}`}>{title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed" data-testid={`brand-trust-card-copy-${kebab(title)}`}>{copy}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 bg-white" data-testid="brand-reviews-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="grid lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-4">
                <div className="flex items-center gap-2 mb-4" data-testid="brand-google-rating-summary"><span className="text-2xl font-extrabold">{googleReviewsData.rating}/5</span>{Array.from({ length: 5 }, (_, starNumber) => starNumber + 1).map((starNumber) => <Star key={`brand-summary-star-${starNumber}`} className="h-4 w-4 text-yellow-400 fill-current" />)}</div>
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3" data-testid="brand-reviews-heading">Repair confidence before you book</h2>
                <p className="text-base text-gray-600" data-testid="brand-reviews-summary">For higher-value printers like Bambu Lab, trust and quote clarity matter before handing over the machine.</p>
              </div>
              <div className="lg:col-span-8 grid md:grid-cols-3 gap-4">
                {googleReviewsData.reviews.map((review) => (
                  <article key={review.id} className="border border-gray-200 bg-gray-50 p-5 rounded-sm" data-testid={`brand-review-card-${review.id}`}>
                    <div className="flex mb-3">{Array.from({ length: review.rating }, (_, starNumber) => starNumber + 1).map((starNumber) => <Star key={`brand-${review.id}-star-${starNumber}`} className="h-4 w-4 text-yellow-400 fill-current" />)}</div>
                    <p className="text-sm text-gray-700 leading-relaxed mb-4" data-testid={`brand-review-content-${review.id}`}>“{review.content}”</p>
                    <p className="text-sm font-bold" data-testid={`brand-review-author-${review.id}`}>{review.name}</p>
                    <p className="text-xs text-gray-500">Google Review</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 bg-gray-50 border-y border-gray-200" data-testid="brand-faq-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-4">
                <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3" data-testid="brand-faq-heading">Bambu Lab repair questions</h2>
                <p className="text-base text-gray-600" data-testid="brand-faq-summary">Answers for Bambu Lab owners comparing DIY troubleshooting, send-in repair and professional diagnosis.</p>
              </div>
              <div className="lg:col-span-8">
                <Accordion type="single" collapsible className="border-t border-gray-200" data-testid="brand-faq-accordion" onValueChange={(value) => value && trackEvent('faq_open', { faq_id: value, faq_category: `${page.slug}_seo_page` })}>
                  {page.faqs.map((faq, index) => (
                    <AccordionItem key={faq.question} value={`${page.slug}-faq-${index + 1}`} className="border-b border-gray-200" data-testid={`brand-faq-item-${index + 1}`}>
                      <AccordionTrigger className="text-left py-4 hover:no-underline" data-testid={`brand-faq-question-${index + 1}`}><span className="text-sm font-extrabold text-gray-900 pr-4">{faq.question}</span></AccordionTrigger>
                      <AccordionContent className="text-sm text-gray-600 leading-relaxed pb-5" data-testid={`brand-faq-answer-${index + 1}`}>{faq.answer}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>
          </div>
        </section>

        <TechnicianNetworkCta location={`${page.slug}-brand-page`} />

        <section className="py-14 bg-gray-900 text-white" data-testid="brand-final-cta-section">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 text-center">
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3" data-testid="brand-final-cta-heading">Need Bambu Lab repair support?</h2>
            <p className="text-base text-gray-400 mb-6" data-testid="brand-final-cta-summary">Start a repair request and include your Bambu model, AMS setup if relevant, symptoms and photos.</p>
            <button onClick={() => scrollToBooking(`${page.slug}_final_cta`, 'Start Bambu repair request')} className="bg-white text-gray-900 hover:bg-gray-100 text-sm font-extrabold px-6 py-3 rounded-sm border-b-4 border-orange-500 hover:border-orange-400 transition-colors" data-testid="brand-final-cta-button">Start Bambu repair request</button>
          </div>
        </section>
      </main>

      <footer className="bg-gray-900 text-white py-14 border-t border-gray-800" data-testid="brand-footer">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <img src={companyInfo.logo} alt={companyInfo.name} className="h-10 w-auto filter brightness-0 invert mb-4" data-testid="brand-footer-logo" />
              <p className="text-gray-400 text-sm leading-relaxed" data-testid="brand-footer-description">Australia&apos;s trusted 3D printer service and repair team.</p>
            </div>
            <div>
              <h3 className="text-base font-extrabold mb-4" data-testid="brand-footer-contact-heading">Contact</h3>
              <div className="space-y-3 text-sm text-gray-300">
                <p className="flex items-start gap-3" data-testid="brand-footer-address"><MapPin className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />{contactInfo.address}</p>
                <a href="tel:1300334327" onClick={() => handlePhoneClick(`${page.slug}_footer`)} className="flex items-center gap-3 hover:text-white" data-testid="brand-footer-phone-link"><Phone className="h-4 w-4 text-gray-500 flex-shrink-0" />{contactInfo.phone} ({contactInfo.phoneOption})</a>
                <a href="mailto:admin@3dprintergear.com.au" onClick={() => trackEvent('email_click', { email_location: `${page.slug}_footer` })} className="flex items-center gap-3 hover:text-white" data-testid="brand-footer-email-link"><Mail className="h-4 w-4 text-gray-500 flex-shrink-0" />admin@3dprintergear.com.au</a>
              </div>
            </div>
            <div>
              <h3 className="text-base font-extrabold mb-4" data-testid="brand-footer-service-pages-heading">Service pages</h3>
              <div className="space-y-2 text-sm text-gray-300">
                <Link to="/" className="block hover:text-white" data-testid="brand-footer-national-link">3D Printer Repair Australia</Link>
                <Link to="/3d-printer-repair-melbourne" className="block hover:text-white" data-testid="brand-footer-melbourne-link">3D Printer Repair Melbourne</Link>
                <Link to="/3d-printer-repair-sydney" className="block hover:text-white" data-testid="brand-footer-sydney-link">3D Printer Repair Sydney</Link>
                <Link to="/3d-printer-repair-brisbane" className="block hover:text-white" data-testid="brand-footer-brisbane-link">3D Printer Repair Brisbane</Link>
                <Link to="/3d-printer-repair-perth" className="block hover:text-white" data-testid="brand-footer-perth-link">3D Printer Repair Perth</Link>
                <Link to="/3d-printer-repair-adelaide" className="block hover:text-white" data-testid="brand-footer-adelaide-link">3D Printer Repair Adelaide</Link>
                <Link to={page.path} className="block hover:text-white" data-testid="brand-footer-current-brand-link">{page.h1}</Link>
                <Link to="/3d-printer-blob-of-death-repair" className="block hover:text-white" data-testid="brand-footer-blob-of-death-link">Blob of Death Repair</Link>
                <Link to="/3d-printer-nozzle-clog-repair" className="block hover:text-white" data-testid="brand-footer-nozzle-clog-link">Nozzle Clog Repair</Link>
                <Link to="/3d-printer-layer-shifting-repair" className="block hover:text-white" data-testid="brand-footer-layer-shifting-link">Layer Shifting Repair</Link>
                <Link to="/3d-printer-bed-adhesion-repair" className="block hover:text-white" data-testid="brand-footer-bed-adhesion-link">Bed Adhesion Repair</Link>
                <Link to="/3d-printer-under-extrusion-repair" className="block hover:text-white" data-testid="brand-footer-under-extrusion-link">Under-Extrusion Repair</Link>
                <Link to="/3d-printer-repairs-for-schools" className="block hover:text-white" data-testid="brand-footer-school-repairs-link">3D Printer Repairs for Schools</Link>
                <Link to="/3d-printer-maintenance-for-schools" className="block hover:text-white" data-testid="brand-footer-school-maintenance-link">3D Printer Maintenance for Schools</Link>
                <Link to="/apply-as-technician" className="block hover:text-white" data-testid="brand-footer-apply-as-technician-link">Apply as a Technician</Link>
              </div>
            </div>
            <div>
              <h3 className="text-base font-extrabold mb-4" data-testid="brand-footer-hours-heading">Hours</h3>
              <div className="text-sm text-gray-300 space-y-1 mb-5" data-testid="brand-footer-business-hours"><p>{contactInfo.businessHours.weekdays}</p><p>{contactInfo.businessHours.saturday}</p><p>{contactInfo.businessHours.sunday}</p></div>
              <button onClick={() => scrollToBooking(`${page.slug}_footer`, 'Book Service')} className="w-full bg-white text-gray-900 hover:bg-gray-100 text-sm font-extrabold px-5 py-2.5 rounded-sm border-b-4 border-orange-500 hover:border-orange-400 transition-colors" data-testid="brand-footer-book-button">Book Service</button>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-10 pt-6 text-sm text-gray-500" data-testid="brand-footer-copyright">{contactInfo.copyright} {companyInfo.name}. All rights reserved. | {contactInfo.abn} | <a href="https://www.3dprintergear.com.au/privacy-policy/" className="hover:text-gray-300 underline underline-offset-2" data-testid="brand-footer-privacy-policy-link">Privacy Policy</a></div>
        </div>
      </footer>

      {!formInView && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 p-3" data-testid="brand-mobile-sticky-cta-wrap">
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <button onClick={() => scrollToBooking(`${page.slug}_mobile_sticky`, 'Start Repair Form')} className="bg-gray-900 hover:bg-black text-white text-sm font-extrabold py-3 rounded-sm border-b-4 border-orange-500 hover:border-orange-400 transition-colors" data-testid="brand-mobile-sticky-start-form-button">Start Repair Form</button>
            <a href="tel:1300334327" onClick={() => handlePhoneClick(`${page.slug}_mobile_sticky`)} className="border border-gray-300 text-gray-900 text-sm font-extrabold px-4 py-3 rounded-sm" data-testid="brand-mobile-sticky-call-button">Call Option 2</a>
          </div>
        </div>
      )}
    </div>
  );
}